import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Workflow Management Hook
 * Handles creating, maintaining, and updating workflow objects for the report builder
 */
export const useWorkflowManager = () => {
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const workflowIdCounter = useRef(1);

  // Generate a unique workflow ID
  const generateWorkflowId = useCallback(() => {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    return `workflow-${timestamp}-${randomId}`;
  }, []);

  // Generate a unique workflow name
  const generateWorkflowName = useCallback(() => {
    const now = new Date();
    const dateStr = now.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timeStr = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `Workflow ${dateStr} ${timeStr}`;
  }, []);

  // Create a new workflow
  const createWorkflow = useCallback(
    (initialNodes = [], initialEdges = []) => {
      const workflowId = `workflow-${Date.now()}`;
      const now = new Date();
      const dateStr = now.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
      const workflowName = `Workflow ${dateStr} ${timeStr}`;

      const workflow = {
        id: workflowId,
        name: workflowName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1,
        status: "draft", // draft, running, completed, error
        nodes: initialNodes,
        edges: initialEdges,
        metadata: {
          nodeCount: initialNodes.length,
          edgeCount: initialEdges.length,
          lastExecutedAt: null,
          executionCount: 0,
        },
      };

      console.log("🆕 Created new workflow:", workflow.id, workflow.name);
      setCurrentWorkflow(workflow);
      return workflow;
    },
    [] // Remove dependencies to make function stable
  );

  // Update workflow with new nodes/edges
  const updateWorkflow = useCallback(
    (nodes, edges) => {
      setCurrentWorkflow((prevWorkflow) => {
        if (!prevWorkflow) {
          // If no workflow exists and we have nodes, create one
          if (nodes.length > 0) {
            const newWorkflow = {
              id: `workflow-${Date.now()}`,
              name: "Untitled Workflow",
              description: "",
              status: "idle",
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              version: 1,
              nodes: nodes,
              edges: edges,
              metadata: {
                nodeCount: nodes.length,
                edgeCount: edges.length,
                tags: [],
                category: "report",
              },
            };
            console.log("🆕 Created new workflow:", newWorkflow.id);
            return newWorkflow;
          }
          return null;
        }

        const updatedWorkflow = {
          ...prevWorkflow,
          nodes: nodes,
          edges: edges,
          updatedAt: new Date().toISOString(),
          version: prevWorkflow.version + 1,
          metadata: {
            ...prevWorkflow.metadata,
            nodeCount: nodes.length,
            edgeCount: edges.length,
          },
        };

        console.log("📝 Updated workflow:", updatedWorkflow.id, {
          nodes: nodes.length,
          edges: edges.length,
          version: updatedWorkflow.version,
        });

        return updatedWorkflow;
      });
    },
    [] // Remove dependencies to make function stable
  );

  // Update workflow name
  const updateWorkflowName = useCallback(
    (newName) => {
      if (!currentWorkflow) return null;

      const updatedWorkflow = {
        ...currentWorkflow,
        name: newName,
        updatedAt: new Date().toISOString(),
        version: currentWorkflow.version + 1,
      };

      console.log("✏️ Updated workflow name:", updatedWorkflow.id, newName);
      setCurrentWorkflow(updatedWorkflow);
      return updatedWorkflow;
    },
    [currentWorkflow]
  );

  // Update workflow status
  const updateWorkflowStatus = useCallback(
    (status, metadata = {}) => {
      if (!currentWorkflow) return null;

      const updatedWorkflow = {
        ...currentWorkflow,
        status: status,
        updatedAt: new Date().toISOString(),
        metadata: {
          ...currentWorkflow.metadata,
          ...metadata,
          ...(status === "running" && { lastExecutedAt: new Date().toISOString() }),
          ...(status === "completed" && {
            executionCount: (currentWorkflow.metadata.executionCount || 0) + 1,
          }),
        },
      };

      console.log("🔄 Updated workflow status:", updatedWorkflow.id, status, metadata);
      setCurrentWorkflow(updatedWorkflow);
      return updatedWorkflow;
    },
    [currentWorkflow]
  );

  // Get workflow payload for API calls
  const getWorkflowPayload = useCallback(() => {
    if (!currentWorkflow) return null;

    // Transform nodes into NodeExecution format for backend
    const nodeExecutions = currentWorkflow.nodes.map((node) => ({
      Id: node.id, // Backend expects capitalized 'Id'
      SelectedNode: {
        // Backend Node model properties
        Id: node.id,
        Name: node.data?.name || node.data?.label || `Node ${node.id}`,
        Type: node.type || node.data?.type,
        Category: node.data?.category || "default",
        Label: node.data?.label || node.data?.name || `Node ${node.id}`,
        Description: node.data?.description || "",
        Icon: node.data?.icon || "",
        Model: node.data?.model || {},
        ExecutionApi: node.data?.execution || "ai/execution/executeNode",
        Output: JSON.stringify(node.data?.output || {}), // Backend expects Output as JSON string
        Input: JSON.stringify(
          currentWorkflow.edges
            .filter((edge) => edge.target === node.id)
            .reduce((acc, edge) => {
              const sourceNode = currentWorkflow.nodes.find((n) => n.id === edge.source);
              if (sourceNode) {
                acc[edge.source] = sourceNode.data?.output || {};
              }
              return acc;
            }, {})
        ),
        RequiresSettings: node.data?.requiresSettings || false,
        Position: node.position,
        Settings: node.data?.model?.settings || {},
        Version: node.data?.version || "1.0",
        Status: node.data?.status || "idle",
      },
    }));

    return {
      Id: currentWorkflow.id,
      Name: currentWorkflow.name,
      NodeExecutions: nodeExecutions,
      Status: currentWorkflow.status,
      Version: currentWorkflow.version,
      CreatedAt: currentWorkflow.createdAt,
      UpdatedAt: currentWorkflow.updatedAt,
    };
  }, [currentWorkflow]);

  // Clear current workflow
  const clearWorkflow = useCallback(() => {
    console.log("🗑️ Cleared workflow:", currentWorkflow?.id);
    setCurrentWorkflow(null);
  }, [currentWorkflow]);

  // Get workflow info for display
  const getWorkflowInfo = useCallback(() => {
    if (!currentWorkflow) return null;

    return {
      id: currentWorkflow.id,
      name: currentWorkflow.name,
      status: currentWorkflow.status,
      nodeCount: currentWorkflow.metadata.nodeCount,
      edgeCount: currentWorkflow.metadata.edgeCount,
      createdAt: currentWorkflow.createdAt,
      updatedAt: currentWorkflow.updatedAt,
      version: currentWorkflow.version,
      executionCount: currentWorkflow.metadata.executionCount,
      lastExecutedAt: currentWorkflow.metadata.lastExecutedAt,
    };
  }, [currentWorkflow]);

  return {
    // State
    currentWorkflow,

    // Actions
    createWorkflow,
    updateWorkflow,
    updateWorkflowName,
    updateWorkflowStatus,
    clearWorkflow,

    // Getters
    getWorkflowPayload,
    getWorkflowInfo,

    // Utilities
    generateWorkflowId,
    generateWorkflowName,
  };
};
